import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function MailServerSettings({ authToken }) {
  const { toast } = useToast();

  const [mailServers, setMailServers] = useState([]);
  const [mailForm, setMailForm] = useState({
    name: "",
    from_email: "",
    reply_to: "",
    host: "",
    port: 587,
    username: "",
    password: "",
    use_tls: true,
    is_default: true,
  });
  const [testingMail, setTestingMail] = useState(false);
  const [editingId, setEditingId] = useState(null);

  function authHeaders() {
    return authToken
      ? {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      : {};
  }

  async function loadMailServers() {
    if (!authToken) return;
    try {
      const res = await axios.get(`${API_BASE}/api/mail-servers`, authHeaders());
      setMailServers(res.data);
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Laden der Mailserver", variant: "destructive" });
    }
  }

  useEffect(() => {
    loadMailServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  async function handleMailServerTest() {
    if (!authToken) return;
    setTestingMail(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/mail-servers/test`,
        {
          name: mailForm.name || "Test-Server",
          from_email: mailForm.from_email,
          reply_to: mailForm.reply_to || undefined,
          host: mailForm.host,
          port: Number(mailForm.port) || 587,
          username: mailForm.username,
          password: mailForm.password,
          use_tls: mailForm.use_tls,
          is_default: false,
        },
        authHeaders(),
      );
      const sentTo = response.data?.sent_to || "Ihre E-Mail-Adresse";
      toast({ title: "Test-E-Mail versendet", description: `Gesendet an: ${sentTo}` });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Mailserver konnte nicht erreicht werden.";
      toast({ title: "Test fehlgeschlagen", description: msg, variant: "destructive" });
    } finally {
      setTestingMail(false);
    }
  }

  function resetForm() {
    setMailForm({
      name: "",
      from_email: "",
      reply_to: "",
      host: "",
      port: 587,
      username: "",
      password: "",
      use_tls: true,
      is_default: true,
    });
    setEditingId(null);
  }

  function startEdit(server) {
    setEditingId(server.id);
    setMailForm({
      name: server.name || "",
      from_email: server.from_email || "",
      reply_to: server.reply_to || "",
      host: server.host || "",
      port: server.port || 587,
      username: server.username || "",
      password: "",
      use_tls: server.use_tls ?? true,
      is_default: server.is_default ?? false,
    });
  }

  async function handleDelete(server) {
    if (!authToken) return;
    const confirmed = window.confirm(
      `Möchten Sie den Mailserver "${server.name}" wirklich löschen?`,
    );
    if (!confirmed) return;

    try {
      await axios.delete(`${API_BASE}/api/mail-servers/${server.id}`, authHeaders());
      setMailServers((prev) => prev.filter((s) => s.id !== server.id));
      if (editingId === server.id) {
        resetForm();
      }
      toast({ title: "Mailserver gelöscht" });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Mailserver konnte nicht gelöscht werden.";
      toast({ title: "Fehler", description: msg, variant: "destructive" });
    }
  }

  async function handleMailServerSave(e) {
    e.preventDefault();
    if (!authToken) return;
    try {
      const payload = {
        name: mailForm.name,
        from_email: mailForm.from_email,
        reply_to: mailForm.reply_to || undefined,
        host: mailForm.host,
        port: Number(mailForm.port) || 587,
        username: mailForm.username,
        password: mailForm.password || undefined,
        use_tls: mailForm.use_tls,
        is_default: mailForm.is_default,
      };

      if (editingId) {
        const res = await axios.put(
          `${API_BASE}/api/mail-servers/${editingId}`,
          payload,
          authHeaders(),
        );
        toast({ title: "Mailserver aktualisiert" });
        setMailServers((prev) => prev.map((s) => (s.id === editingId ? res.data : s)));
      } else {
        const res = await axios.post(
          `${API_BASE}/api/mail-servers`,
          payload,
          authHeaders(),
        );
        toast({ title: "Mailserver gespeichert" });
        setMailServers((prev) => [...prev, res.data]);
      }

      // Passwort im Formular immer leeren
      setMailForm((f) => ({ ...f, password: "" }));
      setEditingId(null);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Mailserver konnte nicht gespeichert werden.";
      toast({ title: "Fehler", description: msg, variant: "destructive" });
    }
  }

  return (
    <Card className="border-slate-200 bg-white/90" data-testid="mailserver-card-inner">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-slate-900">
          Mailserver-Einstellungen (nur Super-Admin)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.3fr)]">
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Konfigurierte Mailserver
            </div>
            {mailServers.length === 0 ? (
              <p className="text-xs text-slate-500">Noch kein Mailserver hinterlegt.</p>
            ) : (
              <Table data-testid="mailserver-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Absender</TableHead>
                    <TableHead>Host</TableHead>
                    <TableHead>Port</TableHead>
                    <TableHead>Standard</TableHead>
                    <TableHead>Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mailServers.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="text-xs">{s.name}</TableCell>
                      <TableCell className="text-xs">{s.from_email}</TableCell>
                      <TableCell className="text-xs">{s.host}</TableCell>
                      <TableCell className="text-xs">{s.port}</TableCell>
                      <TableCell className="text-xs">
                        {s.is_default ? (
                          <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                            Standard
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400">
                            
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => startEdit(s)}
                            data-testid={`mailserver-edit-${s.id}`}
                          >
                            Ändern
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => handleDelete(s)}
                            data-testid={`mailserver-delete-${s.id}`}
                          >
                            Löschen
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <form className="space-y-2" onSubmit={handleMailServerSave} data-testid="mailserver-form">
            <div className="grid gap-2 md:grid-cols-2">
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs text-slate-700">Art des Mailservers</Label>
                <div className="text-xs text-slate-600">
                  Klassische Methode (SMTP)
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="mail-name">Name</Label>
                <Input
                  id="mail-name"
                  value={mailForm.name}
                  onChange={(e) => setMailForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="z.B. Google Schwimmkurse"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mail-from">Absender-E-Mail</Label>
                <Input
                  id="mail-from"
                  type="email"
                  value={mailForm.from_email}
                  onChange={(e) => setMailForm((f) => ({ ...f, from_email: e.target.value }))}
                  placeholder="z.B. schwimmausbildung@die-fitmacher.net"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mail-reply">Rückantwort-Adresse (Reply-To)</Label>
                <Input
                  id="mail-reply"
                  type="email"
                  value={mailForm.reply_to}
                  onChange={(e) => setMailForm((f) => ({ ...f, reply_to: e.target.value }))}
                  placeholder="optional, z.B. info@die-fitmacher.net"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mail-host">SMTP-Host</Label>
                <Input
                  id="mail-host"
                  value={mailForm.host}
                  onChange={(e) => setMailForm((f) => ({ ...f, host: e.target.value }))}
                  placeholder="z.B. smtp.office365.com"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mail-port">Port</Label>
                <Input
                  id="mail-port"
                  type="number"
                  value={mailForm.port}
                  onChange={(e) => setMailForm((f) => ({ ...f, port: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mail-user">Benutzername</Label>
                <Input
                  id="mail-user"
                  value={mailForm.username}
                  onChange={(e) => setMailForm((f) => ({ ...f, username: e.target.value }))}
                  placeholder="meist die E-Mail-Adresse"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mail-pass">Passwort</Label>
                <Input
                  id="mail-pass"
                  type="password"
                  value={mailForm.password}
                  onChange={(e) => setMailForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="SMTP-Passwort"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <label className="inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={mailForm.use_tls}
                  onChange={(e) => setMailForm((f) => ({ ...f, use_tls: e.target.checked }))}
                />
                <span>TLS verwenden (empfohlen)</span>
              </label>
              <label className="inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={mailForm.is_default}
                  onChange={(e) => setMailForm((f) => ({ ...f, is_default: e.target.checked }))}
                />
                <span>Als Standard verwenden</span>
              </label>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={testingMail}
                onClick={handleMailServerTest}
                data-testid="mailserver-test-button"
              >
                {testingMail ? "Test wird gesendet..." : "Test-E-Mail senden"}
              </Button>
              <div className="flex items-center gap-2">
                {editingId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={resetForm}
                  >
                    Neu anlegen
                  </Button>
                )}
                <Button
                  type="submit"
                  size="sm"
                  className="rounded-full bg-sky-600 px-4 text-xs font-medium text-white hover:bg-sky-500"
                  data-testid="mailserver-save-button"
                >
                  {editingId ? "Mailserver aktualisieren" : "Mailserver speichern"}
                </Button>
              </div>
            </div>

            <p className="text-[11px] text-slate-500">
              Diese Einstellungen gelten für alle Funktionen, die E-Mails direkt aus dem System versenden (z.B.
              zukünftige Passwort-zurücksetzen-Funktion).
            </p>
          </form>
        </div>
      </CardContent>
    </Card>
  );
}
